import { Select } from '@kaizen/components/future';
import { transformLocaleCodeForDisplay } from './helpers/localeCode.helpers';
import { useIntl } from '@cultureamp/i18n-react-intl';
import supportedLocaleLabels from './locales.json';
import unsupportedLocaleLabels from './unsupportedLocales.json';
import React, { useId } from 'react';

export const localeLabels = {
  ...supportedLocaleLabels,
  ...unsupportedLocaleLabels,
};

export type LocaleCode = keyof typeof localeLabels;

export type LocaleSelectOption = {
  label: JSX.Element;
  value: LocaleCode;
};

export type LocaleSelectorProps = {
  selectedLocale: LocaleCode;
  setSelectedLocale: React.Dispatch<React.SetStateAction<LocaleCode>>;
  supportedLocales: LocaleCode[];
  classNameOverride?: string;
  fullWidth?: boolean;
  onChange?: (value: LocaleCode) => void;
};

const LocaleSelector: React.ComponentType<LocaleSelectorProps> = ({
  selectedLocale,
  setSelectedLocale,
  supportedLocales,
  classNameOverride,
  fullWidth,
  onChange,
}) => {
  const { formatMessage, formatDisplayName } = useIntl();
  const selectId = useId();

  const localeList = supportedLocales.map((localeCode: LocaleCode) => {
    const targetLocale = localeLabels[localeCode];
    const activeLocale = formatDisplayName(
      transformLocaleCodeForDisplay(localeCode),
      {
        type: 'language',
      }
    );

    return {
      label: `${targetLocale} - ${activeLocale}`,
      value: localeCode,
    };
  });

  if (localeList.length <= 1) return null;

  const selectedLocaleOption = localeList.find(
    (l) => l.value === selectedLocale
  );

  return (
    <div className="relative z-50" id={selectId}>
      <Select
        isFullWidth={fullWidth || false}
        portalContainerId={selectId}
        classNameOverride={classNameOverride}
        label={formatMessage({
          id: 'kioskPage.labelLanguageSelection',
          defaultMessage: 'Language',
          description:
            'Label describing the Language selection options in dropdown',
        })}
        items={localeList}
        onSelectionChange={(selectedLocale) => {
          setSelectedLocale(selectedLocale as LocaleCode);
          onChange?.(selectedLocale as LocaleCode);
        }}
        selectedKey={selectedLocaleOption?.value || supportedLocales[0]}
      />
    </div>
  );
};

export default LocaleSelector;
